/**
 * Canonical apartment shape + normalizer (frontend mirror of
 * backend/services/apartment_canonical.py), plus the per-render
 * personalization context (`CardRenderContext`) cards consume
 * alongside it.
 *
 * Read-side code (cards, filters, map markers) should accept these
 * two shapes. Convert raw `Apartment` records via
 * `normalizeApartment(raw)` at the API boundary; build a
 * `CardRenderContext` via `mapToCardRenderContext(raw, currentFilters)`
 * for the same boundary call when personalization data is present.
 * Write-side and store code can stay on the wide `Apartment` shape
 * until incremental migration is done.
 *
 * See ADR-0008 (card-data canonical fields) and
 * backend/services/apartment_canonical.py for the migration plan.
 */
import type { Apartment } from './apartment';

// ── Canonical bed keys ──────────────────────────────────────────────

export type CanonicalBedKey =
  | 'Studio'
  | '1BR'
  | '2BR'
  | '3BR'
  | '4BR'
  | 'Penthouse';

export const CANONICAL_BED_KEYS: readonly CanonicalBedKey[] = [
  'Studio', '1BR', '2BR', '3BR', '4BR', 'Penthouse',
] as const;

/**
 * Map any frontend representation of a bedroom type to a canonical key.
 *
 * Handles:
 *   0 → 'Studio'; 1..4 → '1BR'..'4BR'
 *   'studio' / 'Studio' / 'Efficiency' → 'Studio'
 *   '1BR' / '1br' / '1 BR' / '1Bed' / 'One Bed' / '1' → '1BR'
 *   'PH' / 'penthouse' → 'Penthouse'
 *
 * Returns null when nothing matches.
 */
export function normalizeBedKey(raw: unknown): CanonicalBedKey | null {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    if (raw === 0) return 'Studio';
    if (raw >= 1 && raw <= 4) return `${raw}BR` as CanonicalBedKey;
    return null;
  }
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  if (['studio', 'efficiency', '0br', '0 br', '0bed', '0 bed', '0'].includes(s)) {
    return 'Studio';
  }
  if (s === 'penthouse' || s === 'ph') return 'Penthouse';
  const m = s.match(/^(\d)\s*(?:br|bed|bedroom)?$/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 4) return `${n}BR` as CanonicalBedKey;
  }
  const wordMap: Record<string, number> = { one: 1, two: 2, three: 3, four: 4 };
  for (const [word, n] of Object.entries(wordMap)) {
    if (s.startsWith(word)) return `${n}BR` as CanonicalBedKey;
  }
  return null;
}

/** Studio before 1BR before 2BR, so a range reads "Studio–1BR" rather than
 *  whichever key the object happened to enumerate first. */
export function sortBedKeys(keys: readonly string[]): string[] {
  const rank = (key: string): number => {
    const canonical = normalizeBedKey(key);
    const index = canonical ? CANONICAL_BED_KEYS.indexOf(canonical) : -1;
    return index === -1 ? CANONICAL_BED_KEYS.length : index;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b)));
}

// ── Price parsing ───────────────────────────────────────────────────

/**
 * Coerce a price representation to an integer dollar amount in the
 * sane range $400–$25,000/mo. Returns null for anything else.
 */
export function parsePrice(raw: unknown): number | null {
  if (raw == null || typeof raw === 'boolean') return null;
  if (typeof raw === 'number') {
    const n = Math.trunc(raw);
    return n >= 400 && n <= 25000 ? n : null;
  }
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase().replace(/\$/g, '').replace(/,/g, '');
  if (!s) return null;
  const kMatch = s.match(/^(\d+(?:\.\d+)?)k$/);
  if (kMatch) {
    const n = Math.trunc(parseFloat(kMatch[1]) * 1000);
    return n >= 400 && n <= 25000 ? n : null;
  }
  const n = parseInt(s, 10);
  if (Number.isNaN(n)) return null;
  return n >= 400 && n <= 25000 ? n : null;
}

// ── Display formatting for raw (non-canonical) price maps ───────────
//
// Cards that read the canonical `startingPrices` map (all numbers) don't
// need these — use them only when rendering raw `price_ranges` /
// `starting_prices` / `starting_prices_display` maps straight off an
// Apartment record, which may carry CSV-ingested Houston shapes:
//   - bedroom keys like "studio" / "1_bedroom" (snake_case)
//   - price values as { min, max } range objects (not scalars)
// Without normalization these render as raw keys and "[object Object]".

/**
 * Cheapest canonical starting price, filter-aware: when `preferredBeds`
 * is set and any selected bed type is priced, return the cheapest among
 * THOSE (so a pin/card never advertises a 2BR price under a 1BR filter);
 * otherwise null. With no bedroom selection, return the overall minimum.
 *
 * Single source of truth for every surface that shows "the" price of a
 * building next to a bedroom filter (map pins, mini cards, peek cards) —
 * keep them on this helper so they can't drift apart again.
 */
export function cheapestStartingPrice(
  startingPrices: Partial<Record<CanonicalBedKey, number>>,
  preferredBeds?: string[],
): { value: number; bedKey: CanonicalBedKey; matchedFilter: boolean } | null {
  const entries = Object.entries(startingPrices).filter(
    ([, v]) => typeof v === 'number' && (v as number) > 0,
  ) as Array<[CanonicalBedKey, number]>;
  if (entries.length === 0) return null;
  if (preferredBeds && preferredBeds.length > 0) {
    const wanted = new Set(
      preferredBeds.map((b) => normalizeBedKey(b)).filter(Boolean) as string[],
    );
    const matched = entries.filter(([k]) => wanted.has(k));
    if (matched.length > 0) {
      const [bedKey, value] = matched.reduce((a, b) => (a[1] <= b[1] ? a : b));
      return { value, bedKey, matchedFilter: true };
    }
    return null;
  }
  const [bedKey, value] = entries.reduce((a, b) => (a[1] <= b[1] ? a : b));
  return { value, bedKey, matchedFilter: false };
}

/**
 * Canonical per-bed starting prices straight off a raw Apartment record,
 * for callers that need prices without the full normalizeApartment pass
 * (e.g. building one map-pin label per marker per render).
 */
export function apartmentStartingPrices(
  raw: Apartment | Record<string, unknown>,
): Partial<Record<CanonicalBedKey, number>> {
  return normalizePricing(raw as Record<string, unknown>);
}

/**
 * Format a raw bedroom-map key into a display label, normalizing
 * snake_case Houston keys ("1_bedroom" → "1BR"). Falls back to the
 * de-underscored key when it doesn't map to a canonical bed type.
 */
export function formatBedKey(key: string): string {
  const spaced = key.replace(/_/g, ' ');
  return normalizeBedKey(spaced) || spaced;
}

/**
 * Format a price value of unknown shape for display:
 *   - number / "$x" string → "~$1,234"
 *   - { min, max } range object (Houston CSV) → "$1,200–$1,500" (or
 *     "~$1,200" when only one bound is present / they're equal)
 *   - already-formatted non-numeric string → returned trimmed as-is
 * Returns null when nothing usable can be parsed.
 */
export function formatPriceValue(value: unknown): string | null {
  if (value && typeof value === 'object') {
    const o = value as { min?: unknown; max?: unknown };
    const min = parsePrice(o.min);
    const max = parsePrice(o.max);
    if (min != null && max != null && min !== max) {
      // en dash for ranges (house typography); ~ because DB prices are
      // approximations (CONTEXT.md pricing rules).
      return `~$${min.toLocaleString()}–$${max.toLocaleString()}`;
    }
    const one = min ?? max;
    return one != null ? `~$${one.toLocaleString()}` : null;
  }
  const n = parsePrice(value);
  if (n != null) return `~$${n.toLocaleString()}`;
  if (typeof value === 'string' && value.trim()) {
    // Scraped pages say "Call for pricing" / "Contact for rates"; Taco Street
    // never lists that wording (Alex, 2026-09-30).
    if (/\b(call|contact)\b.*\b(pric|rate)/i.test(value)) return PRICING_UNAVAILABLE_MESSAGE;
    return value.trim();
  }
  return null;
}

/**
 * Coerce a price value of unknown shape to a single numeric dollar
 * amount — scalar via `parsePrice`, or the lower bound of a { min, max }
 * range object. Returns null when nothing parses. Use when a numeric
 * value is needed (cheapest-price headlines, PriceTag) rather than a
 * formatted string.
 */
export function priceValueToNumber(value: unknown): number | null {
  if (value && typeof value === 'object') {
    const o = value as { min?: unknown; max?: unknown };
    return parsePrice(o.min) ?? parsePrice(o.max);
  }
  return parsePrice(value);
}

// ── Canonical types ─────────────────────────────────────────────────

export interface CanonicalFloorplan {
  bedKey: CanonicalBedKey | null;
  planName?: string;
  sqFt?: number;
  bathrooms?: number;
  startingPrice?: number;
}

export interface PhotoAttribution {
  sourceType: 'google' | 'website' | 'legacy';
  /** Google's required attribution anchor HTML — render verbatim, linked. */
  attributionHtml: string | null;
  sourceDomain: string | null;
  sourceUrl: string | null;
}

export interface CanonicalApartment {
  name: string;
  city?: string;
  neighborhood?: string;
  region?: string;
  address?: string;
  latitude?: number;
  longitude?: number;

  /** Fully-resolved photo URLs in priority order. Empty = no data. */
  photos: string[];

  /**
   * Per-photo provenance, index-aligned 1:1 with `photos` (mirrors backend
   * apartment_canonical). attributionHtml is Google's required attribution
   * anchor — render verbatim. Additive: consumers reading only `photos`
   * are unaffected.
   */
  photoAttribution: PhotoAttribution[];

  /**
   * Place-level Google attribution (deduped, ordered) for docs whose
   * google_photos is NOT per-photo aligned with `photos` (P2-backfilled).
   * Non-empty only for not-aligned docs — render this union near the
   * gallery instead of per-photo. Mirrors backend photo_attribution_place.
   */
  photoAttributionPlace: string[];

  /** Canonical pricing map. Missing key = no data for that bed type. */
  startingPrices: Partial<Record<CanonicalBedKey, number>>;

  floorplans: CanonicalFloorplan[];

  yearBuilt?: number;
  totalUnits?: number;
  buildingType?: string;
  walkScore?: number;
  transitScore?: number;

  rating?: number;
  reviewCount?: number;

  /**
   * Whether the Apartment has been Upranked by the curator.
   * Storage rename target per ADR-0008 / CONTEXT.md: `isTacoFavorite`
   * is the legacy name; `isUpranked` is the canonical name. Both
   * present during migration; readers should prefer `isUpranked` when
   * defined and fall back to `isTacoFavorite`.
   */
  isTacoFavorite: boolean;
  isUpranked?: boolean;

  isPetFriendly: boolean | null;

  /**
   * Free-text per-Apartment annotation written by the curator.
   * Rename target per ADR-0008 / CONTEXT.md: legacy `taco_favorite_note`
   * becomes `curatorNote`. Per-Apartment, not per-Renter.
   */
  curatorNote?: string;

  /** Short label for a "Last Known Special" pill, e.g. "6 weeks free". */
  leasingSpecialShort?: string;
  leasingSpecialsRaw?: string;

  /**
   * Community-level features of the building (Amenity per CONTEXT.md):
   * gym, pool, concierge, dog park, rooftop deck. Belongs to the
   * Apartment, not to any one resident's home.
   */
  amenities?: string[];

  /**
   * Features inside the apartment unit itself (Unit feature per
   * CONTEXT.md): W/D in unit, dishwasher, balcony, hardwood floors,
   * walk-in closet.
   */
  unitFeatures?: string[];

  /**
   * Controlled-vocabulary labels used for matching and filtering
   * (Tag per CONTEXT.md): `luxury`, `pet-friendly`, `modern`. Not a
   * physical thing; an abstraction used by Search.
   */
  tags?: string[];

  pricingSource?: string;
  pricingUpdatedAt?: string;
  /**
   * Per-bed provenance for `prices`, keyed identically. `pricingSource` and
   * `pricingUpdatedAt` describe the DOCUMENT's last write and cannot describe
   * a bed whose winning value came from a different tier — read this when the
   * question is "where did THIS number come from". Keyed identically to
   * `startingPrices`; a bed present in one is present in the other.
   */
  startingPricesProvenance: Partial<Record<CanonicalBedKey, CanonicalBedPrice>>;
  pricingUnavailable: boolean;
  /** Stale price string from before invalidation, formatted "~$X,XXX". Only set when pricingUnavailable. */
  stalePriceDisplay?: string;
  pricingInvalidatedReason?: string;
  pricingInvalidatedAt?: string;
}

/**
 * Per-render personalization context for a Search result card.
 *
 * Carries the data that varies by *which renter is viewing this
 * Apartment right now* — Headline + Recommendation (Dossier-
 * personalized; two renters see different values for the same
 * Apartment per CONTEXT.md), the active bedroom criteria that
 * shapes price formatting, and AI-display gating metadata.
 *
 * Cards accept `{ apartment: CanonicalApartment, renderContext?:
 * CardRenderContext }`. The optional shape lets cards render
 * without personalization (saved-list view, admin tools, any
 * surface with no active Search) — in that mode cards show
 * building-cheapest prices and no Headline/Recommendation.
 *
 * Built at the API boundary by `mapToCardRenderContext(raw,
 * currentFilters)` (added in a later PR).
 *
 * See ADR-0008.
 */
export interface CardRenderContext {
  /**
   * Short, personalized, skim-readable single sentence shown on the
   * card. Max ~12 words / ~70 chars per CONTEXT.md. Composed on the
   * backend from the LLM's `bottom_line` field.
   */
  headline: string;

  /**
   * Long-form personalized explanation shown on detail expand.
   * Composed on the backend from the LLM's structured output
   * (`the_vibe` + `why_it_works_for_you` + `the_honest_truth` +
   * `highlights` + `watch_outs`). Replaces the legacy
   * `ai_analysis` / `explanation` / `why_it_works_for_you` /
   * `the_vibe` / `ai_highlights` field cascade.
   */
  recommendation: string;

  /**
   * Active Search criteria bedrooms — the bedroom types the renter
   * is filtering for. Cards use this to prefer those bed keys when
   * picking a headline price via `formatPriceCanonical(apartment,
   * renderContext.bedrooms)`. Absent when there is no active
   * Search (e.g., saved-list view).
   */
  bedrooms?: CanonicalBedKey[];

  /**
   * AI display gating signal: 1 = generic LLM output, 2 =
   * Dossier-personalized. Cards may show different chrome or
   * hide the Recommendation section based on this value.
   */
  aiTier?: number;

  /**
   * Provenance tag for the result's ranking, used by some card
   * surfaces to gate which sections render. Example values:
   * `ai_curated`, `algorithmic`.
   */
  rankingSource?: string;
}

// ── Leasing special parser ──────────────────────────────────────────

const LEASING_SPECIAL_PATTERNS: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/(\d+)\s*weeks?\s*free/i, (m) => `${m[1]} weeks free`],
  [/(\d+)\s*months?\s*free/i, (m) => `${m[1]} months free`],
];

export function parseLeasingSpecial(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  for (const [pattern, formatter] of LEASING_SPECIAL_PATTERNS) {
    const m = raw.match(pattern);
    if (m) return formatter(m);
  }
  return null;
}

/**
 * Returns a displayable "N weeks/months free" label only when the special
 * meets the ≥4-week threshold. Use this on all card surfaces so the same
 * rule applies everywhere — not `parseLeasingSpecial` directly.
 */
export function getDisplaySpecial(raw: unknown): string | null {
  const parsed = parseLeasingSpecial(raw);
  if (!parsed) return null;
  const weeksMatch = parsed.match(/^(\d+)\s*weeks?\s*free$/i);
  if (weeksMatch) {
    return parseInt(weeksMatch[1], 10) >= 4 ? parsed : null;
  }
  return parsed; // months free always passes
}

// ── Photo URL resolver ──────────────────────────────────────────────

export type PhotoPreset = 'thumb' | 'card' | 'hero';

const R2_VARIANT_SUFFIX_RE = /-(?:thumb|card|hero)\.webp$/i;
const LEGACY_STATIC_PHOTO_RE = /^\/static\/photos\/([^/]+)\/photo_(\d+)\.jpe?g$/i;

function r2PublicBase(): string | null {
  const v = (process.env.REACT_APP_R2_PUBLIC_BASE_URL ?? '').replace(/\/+$/, '');
  return v || null;
}

function photoCandidateText(item: unknown): string {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return '';
  const o = item as Record<string, unknown>;
  return ['url', 'src', 'href', 'photo_url', 'source_url', 'alt', 'title', 'caption', 'description', 'source_domain']
    .map((key) => o[key])
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ')
    .toLowerCase();
}

type PhotoContentRole = 'exterior' | 'interior' | 'amenity' | 'unknown';

const PHOTO_REJECT_PATTERNS = [
  /404/i,
  /loader/i,
  /placeholder/i,
  /default/i,
  /special/i,
  /promo/i,
  /promotion/i,
  /leasing/i,
  /lease/i,
  /now[-_ ]?leasing/i,
  /banner/i,
  /hero/i,
  /callout/i,
  /marketing/i,
  /stock/i,
  /logo/i,
  /icon/i,
  /sign/i,
  /contact/i,
  /scheduler/i,
  /tour/i,
  /floorplan/i,
  /site[-_ ]?map/i,
  /map/i,
  /pdf/i,
  /brochure/i,
  /flyer/i,
  /rendering/i,
  /illustration/i,
  /mascot/i,
  /pet[-_ ]?policy/i,
  /\bdog\b/i,
  /\bcat\b/i,
  /\bpuppy\b/i,
  /\bkitten\b/i,
  /\bteam\b/i,
  /\bstaff\b/i,
  /\bpeople\b/i,
] as const;

const PHOTO_EXTERIOR_PATTERNS = [
  /exterior/i,
  /building/i,
  /facade/i,
  /\bfront\b/i,
  /\bstreet(?:view|scape)?\b/i,
  /tower/i,
  /entrance/i,
  /arrival/i,
  /curb/i,
  /aerial/i,
  /drone/i,
  /\bdji/i,           // DJI drone filenames → always an exterior aerial
  /dusk|twilight/i,   // dusk/twilight hero shots are exterior
  /elevation/i,
  /skyline/i,
  /community[-_ ]?exterior/i,
] as const;

const PHOTO_INTERIOR_PATTERNS = [
  /interior/i,
  /living/i,
  /kitchen/i,
  /bedroom/i,
  /bath/i,
  /bathroom/i,
  /unit/i,
  /apartment/i,
  /residence/i,
  /closet/i,
  /laundry/i,
  /balcony/i,
  /patio/i,
] as const;

const PHOTO_AMENITY_PATTERNS = [
  /amenity/i,
  /pool/i,
  /rooftop/i,
  /gym/i,
  /fitness/i,
  /club(?:house)?/i,
  /lobby/i,
  /lounge/i,
  /cowork/i,
  /workspace/i,
  /courtyard/i,
  /dog[-_ ]?park/i,
  /pickleball/i,
  /spa/i,
] as const;

const PHOTO_TINY_FILENAME_RE = /[-_](\d{1,3})w[-._]/i;
const PHOTO_TINY_PARAM_RE = /[?&,/]w_(\d{1,4})(?=[,/&\b?])/i;
const PHOTO_TINY_THRESHOLD = 480;

function photoContentRole(text: string): PhotoContentRole {
  if (PHOTO_EXTERIOR_PATTERNS.some((pattern) => pattern.test(text))) return 'exterior';
  if (PHOTO_INTERIOR_PATTERNS.some((pattern) => pattern.test(text))) return 'interior';
  if (PHOTO_AMENITY_PATTERNS.some((pattern) => pattern.test(text))) return 'amenity';
  return 'unknown';
}

function photoQualityScore(item: unknown): number {
  const text = photoCandidateText(item);
  if (!text) return 0;

  if (PHOTO_REJECT_PATTERNS.some((pattern) => pattern.test(text))) return -1000;
  const tinyFilename = text.match(PHOTO_TINY_FILENAME_RE);
  if (tinyFilename && parseInt(tinyFilename[1], 10) < PHOTO_TINY_THRESHOLD) return -1000;
  const tinyParam = text.match(PHOTO_TINY_PARAM_RE);
  if (tinyParam && parseInt(tinyParam[1], 10) < PHOTO_TINY_THRESHOLD) return -1000;

  let score = 0;
  const role = photoContentRole(text);
  if (role === 'exterior') score += 56;
  if (role === 'interior') score += 26;
  if (role === 'amenity') score += 16;

  if (/gallery/i.test(text)) score += 8;
  if (/view/i.test(text)) score += 6;
  if (/hero/i.test(text)) score -= 18;
  if (/thumbnail|thumb/i.test(text)) score -= 20;
  if (/blur|blurry|fuzzy/i.test(text)) score -= 40;

  // Prefer ordinary numbered gallery shots over obvious callout assets.
  const indexMatch = text.match(/(?:^|[\/_-])(image|img|gallery|photo)?[-_ ]?(\d{1,3})(?:[^\d]|$)/i);
  if (indexMatch) {
    score += Math.max(0, 12 - parseInt(indexMatch[2], 10));
  }

  return score;
}

/**
 * Normalize a photo entry into a fully-resolved URL (ADR-0010 post-PR7 mirror
 * of backend `_resolve_photo_url`).
 *
 * With REACT_APP_R2_PUBLIC_BASE_URL configured:
 *   <r2>/photos/<id>/<N>           → <r2>/photos/<id>/<N>-<preset>.webp
 *   <r2>/photos/<id>/<N>-X.webp   → pass through unchanged
 *   other http(s)                 → pass through (Google Places etc.)
 *   /static/photos/<id>/photo_N    → <r2>/photos/<id>/N-<preset>.webp
 *   dict {url|src|href|photo_url} → recurse
 */
export function resolvePhotoUrl(raw: unknown, preset: PhotoPreset = 'card'): string | null {
  if (typeof raw === 'string' && raw) {
    const r2Base = r2PublicBase();
    const legacyMatch = raw.match(LEGACY_STATIC_PHOTO_RE);
    if (legacyMatch && r2Base) {
      const [, aptId, idx] = legacyMatch;
      return `${r2Base}/photos/${aptId}/${idx}-${preset}.webp`;
    }
    if (r2Base && raw.startsWith(r2Base)) {
      // SVG floorplans stored as-is — pass through, no -<preset>.webp.
      if (raw.toLowerCase().endsWith('.svg')) return raw;
      if (R2_VARIANT_SUFFIX_RE.test(raw)) return raw;
      return `${raw}-${preset}.webp`;
    }
    return raw;
  }
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    for (const key of ['url', 'src', 'href', 'photo_url']) {
      const v = r[key];
      if (typeof v === 'string' && v) return resolvePhotoUrl(v, preset);
    }
  }
  return null;
}

/**
 * Given a resolved R2 variant URL (…/<N>-card.webp), return the OTHER preset
 * variants as fallbacks, so a photo whose `card` variant was never uploaded
 * (partial R2 migration / partial upload) recovers from `hero`/`thumb` instead
 * of showing as broken. Returns [] for non-variant URLs (external/Google/SVG).
 */
const PRESET_FALLBACK_ORDER: PhotoPreset[] = ['hero', 'card', 'thumb'];
export function altPresetUrls(url: string): string[] {
  if (typeof url !== 'string') return [];
  const m = url.match(/-(thumb|card|hero)\.webp$/i);
  if (!m || m.index === undefined) return [];
  const current = m[1].toLowerCase();
  const base = url.slice(0, m.index);
  return PRESET_FALLBACK_ORDER
    .filter((p) => p !== current)
    .map((p) => `${base}-${p}.webp`);
}

// ── Pet-friendly derivation ─────────────────────────────────────────

function derivePetFriendly(raw: Record<string, unknown>): boolean | null {
  if (raw.pets_allowed === true) return true;
  if (raw.dogs_allowed === true || raw.allows_dogs === true) return true;
  if (raw.is_pet_friendly === true) return true;
  if (raw.pets_allowed === false) return false;
  if (raw.allows_dogs === false) return false;
  const policy = raw.pet_policy;
  if (typeof policy === 'string' && policy.trim()) {
    if (/no\s+pet|not\s+allowed|prohibited/i.test(policy)) return false;
    if (/pet|dog|cat|welcome|friendly|allowed/i.test(policy)) return true;
  }
  return null;
}

// ── Pricing normalization ───────────────────────────────────────────

// Mirrors backend services/apartment_canonical._normalize_pricing — keep
// the trust order identical so SSR and client agree (project decision
// 2026-05-18). Order: manual hand-correction > fresh firedev floorplans
// > firedev scalar price_ranges > legacy floorplans[] > other helper
// starting_prices > peer/estimated > legacy price_ranges{min} dict.
const MANUAL_PRICING_SOURCES = new Set(['manual', 'manual_edit']);
const ESTIMATED_PRICING_SOURCES = new Set(['estimated', 'peer_estimated']);
// Renter-facing wording for a building with no price (Alex, 2026-09-30):
// never "Call for pricing" or "Contact for pricing".
export const PRICING_UNAVAILABLE_MESSAGE = 'Pricing not available';

export function isPricingUnavailable(raw: Apartment | Record<string, unknown>): boolean {
  const r = raw as Record<string, unknown>;
  const source = String(r.pricing_source ?? '').trim().toLowerCase();
  // pricing_needs_reenrichment is a background-task signal (re-fetch when possible),
  // not a data-quality signal — the existing starting_prices are still valid to show.
  // Only suppress prices when they were explicitly invalidated/wiped.
  return source === 'invalidated';
}

// Mirrors backend is_broadcast_starting_prices / is_templated_starting_prices.
// Broadcast = 2+ bed types all the same number (one headline stamped across
// beds). Templated = 2+ values, all round multiples of $500, evenly spaced.
function isBroadcastPrices(vals: number[]): boolean {
  return vals.length >= 2 && new Set(vals).size === 1;
}
function isTemplatedPrices(vals: number[]): boolean {
  if (vals.length < 2) return false;
  const s = [...vals].sort((a, b) => a - b);
  const diffs = s.slice(1).map((v, i) => v - s[i]);
  if (!diffs.every((d) => d === diffs[0]) || diffs[0] === 0) return false;
  // Strongest signal: the old perfectly round $500 ladder.
  if (s.every((v) => v % 500 === 0) && diffs[0] % 500 === 0) return true;
  // Generic enrichment ladders like 1200/1500/1800 — 3+ points, all $50
  // multiples, at least two $100 multiples, step $100–$900. Mirrors the
  // backend's second branch; without it the backend suppressed a ladder
  // the web still rendered as real.
  if (s.length < 3) return false;
  if (!s.every((v) => v % 50 === 0)) return false;
  if (s.filter((v) => v % 100 === 0).length < 2) return false;
  return diffs[0] >= 100 && diffs[0] <= 900;
}

/**
 * Where one bed's winning price came from, and when — NOT the building's
 * last-write stamp.
 *
 * `pricingSource` / `pricingUpdatedAt` describe the document, and a document
 * routinely mixes tiers: The Beverly serves a manual 1BR alongside firedev
 * floorplan rows, so stamping "manual_edit, September 2" on every bed
 * attributes the manual edit to numbers nobody edited. The provenance below
 * travels with the value that actually won.
 *
 * `asOf` is deliberately absent for the floorplan tiers. Those arrays carry
 * no date of their own, and borrowing `pricing_updated_at` for them is the
 * precise mis-attribution this type exists to prevent — a price of unknown
 * age must read as unknown, not as fresh.
 */
export type CanonicalBedPriceSource =
  | 'server'
  | 'manual'
  | 'firedev_floorplans'
  | 'price_ranges'
  | 'legacy_floorplans'
  | 'starting_prices'
  | 'estimated'
  | 'legacy_price_ranges_min';

export interface CanonicalBedPrice {
  value: number;
  /** Which tier of the precedence ladder produced this number. */
  source: CanonicalBedPriceSource;
  /** ISO date of the write that produced it, when the tier has one. */
  asOf?: string;
}

/** Per-bed prices with the provenance of each winning value attached. */
export function normalizePricingDetailed(
  raw: Record<string, unknown>,
): Partial<Record<CanonicalBedKey, CanonicalBedPrice>> {
  if (isPricingUnavailable(raw)) return {};

  // The server's answer wins. Every search/map/vibe payload carries
  // `canonical_prices` from the backend trust walk (2026-09-24); the walk
  // below is the same rule re-implemented by hand, kept as a fallback for
  // payloads that predate the field (saved lists persisted in the browser).
  // An empty object is an answer — unknown or suppressed — not a miss.
  const server = raw.canonical_prices;
  if (server && typeof server === 'object' && !Array.isArray(server)) {
    const fromServer: Partial<Record<CanonicalBedKey, CanonicalBedPrice>> = {};
    const stamp = typeof raw.pricing_updated_at === 'string' ? raw.pricing_updated_at : undefined;
    for (const [k, v] of Object.entries(server as Record<string, unknown>)) {
      const bed = normalizeBedKey(k);
      const price = parsePrice(v);
      if (bed && price != null) {
        fromServer[bed] = stamp ? { value: price, source: 'server', asOf: stamp } : { value: price, source: 'server' };
      }
    }
    return fromServer;
  }

  const out: Partial<Record<CanonicalBedKey, CanonicalBedPrice>> = {};
  // Only the starting_prices family is dated by pricing_updated_at: that
  // field records the write that produced those maps.
  const mapDate = typeof raw.pricing_updated_at === 'string'
    ? raw.pricing_updated_at : undefined;

  const add = (bedRaw: unknown, priceRaw: unknown,
               source: CanonicalBedPriceSource, asOf?: string) => {
    const bed = normalizeBedKey(bedRaw);
    const price = parsePrice(priceRaw);
    if (bed && price != null && out[bed] == null) {
      out[bed] = asOf ? { value: price, source, asOf } : { value: price, source };
    }
  };

  const addStarting = (key: string, source: CanonicalBedPriceSource) => {
    const m = raw[key] as Record<string, unknown> | undefined;
    if (m) for (const [k, v] of Object.entries(m)) add(k, v, source, mapDate);
  };

  const addFloorplans = (fps: unknown, source: CanonicalBedPriceSource) => {
    if (!Array.isArray(fps)) return;
    // Per-bed minimum WITHIN this list, then apply via first-wins `add`
    // so a later (lower-trust) floorplan pass can't undercut an earlier
    // (higher-trust) pass.
    const mins: Partial<Record<CanonicalBedKey, number>> = {};
    for (const fp of fps) {
      if (!fp || typeof fp !== 'object') continue;
      const f = fp as Record<string, unknown>;
      const bed = normalizeBedKey(f.bedrooms) || normalizeBedKey(f.category);
      const price = parsePrice(f.starting_price);
      if (bed && price != null) {
        const cur = mins[bed];
        if (cur == null || price < cur) mins[bed] = price;
      }
    }
    for (const [bed, price] of Object.entries(mins)) add(bed, price, source);
  };

  const source = String(raw.pricing_source ?? '').trim().toLowerCase();
  const isManual = MANUAL_PRICING_SOURCES.has(source);
  const isEstimated = ESTIMATED_PRICING_SOURCES.has(source);

  // `starting_prices_display` is DERIVED presentation, not a source. It was
  // read at tiers 1/5/6 alongside the dict; because `add` is first-wins it
  // contributed nothing while the two agreed and silently overrode the dict
  // when they disagreed — the state a write-time guard creates (Northshore
  // kept a Studio the guard had dropped). The backend stopped reading it in
  // PR #303; this mirror lagged a week.

  // 1. Manual hand-correction wins outright.
  if (isManual) {
    addStarting('starting_prices', 'manual');
  }

  // 2. Fresh firedev floorplan scrape — accurate per-unit data.
  addFloorplans(raw.firedev_floorplans, 'firedev_floorplans');

  // 3. Scalar price_ranges (snake_case ints, firedev-derived). De-underscore
  //    keys ("1_bedroom" → "1 bedroom") so normalizeBedKey can map them.
  const pr = raw.price_ranges as Record<string, unknown> | undefined;
  if (pr) {
    for (const [k, v] of Object.entries(pr)) {
      if (!(v && typeof v === 'object')) {
        add(k.replace(/_/g, ' '), v, 'price_ranges', mapDate);
      }
    }
  }

  // 4. Legacy floorplans[] — older pipeline but real per-unit prices;
  //    trusted above hallucinated/estimated headline starting_prices.
  addFloorplans(raw.floorplans, 'legacy_floorplans');

  // 5. Other helper starting_prices (external / firecrawl / unknown).
  if (!isManual && !isEstimated) {
    addStarting('starting_prices', 'starting_prices');
  }

  // 6. Peer/estimated starting_prices — weakest priced source.
  if (isEstimated) {
    addStarting('starting_prices', 'estimated');
  }

  // 7. Legacy price_ranges{min} dict — pre-ADR-0009, cross-property
  //    contamination-prone; dead last.
  if (pr) {
    for (const [k, v] of Object.entries(pr)) {
      if (v && typeof v === 'object') {
        add(k, (v as { min?: unknown }).min, 'legacy_price_ranges_min', mapDate);
      }
    }
  }

  // Phase D: never serve a fabricated price. Suppress a broadcast or
  // templated map entirely unless it is a human correction (manual is
  // ground truth, never suppressed). Mirrors backend _normalize_pricing.
  const vals = Object.values(out)
    .filter((v): v is CanonicalBedPrice => v != null)
    .map((v) => v.value);
  if (!isManual && vals.length > 0 && (isBroadcastPrices(vals) || isTemplatedPrices(vals))) {
    return {};
  }

  return out;
}

/** Drop the provenance, keep the numbers. */
function canonicalPricesOf(
  detailed: Partial<Record<CanonicalBedKey, CanonicalBedPrice>>,
): Partial<Record<CanonicalBedKey, number>> {
  const out: Partial<Record<CanonicalBedKey, number>> = {};
  for (const [bed, entry] of Object.entries(detailed)) {
    if (entry) out[bed as CanonicalBedKey] = (entry as CanonicalBedPrice).value;
  }
  return out;
}

/**
 * Per-bed prices as bare numbers. Thin projection of
 * `normalizePricingDetailed` — every consumer that only needs the number
 * keeps working unchanged, and the two can never disagree about which
 * value won because there is only one ladder.
 */
function normalizePricing(raw: Record<string, unknown>): Partial<Record<CanonicalBedKey, number>> {
  return canonicalPricesOf(normalizePricingDetailed(raw));
}

function toFloorplan(fp: Record<string, unknown>): CanonicalFloorplan {
  return {
    bedKey: normalizeBedKey(fp.bedrooms) || normalizeBedKey(fp.category),
    planName: (fp.plan_name as string) || (fp.name as string) || undefined,
    sqFt: (fp.sq_ft as number) ?? (fp.sqft_min as number) ?? undefined,
    bathrooms: fp.bathrooms as number | undefined,
    startingPrice: parsePrice(fp.starting_price) ?? undefined,
  };
}

/** Identity for de-duplicating the same unit appearing in both plan arrays. */
function floorplanIdentity(fp: CanonicalFloorplan): string {
  const name = (fp.planName || '').trim().toLowerCase();
  // A named plan is identified by its name; an unnamed one by its shape,
  // so two unnamed 1BRs of different sizes stay two floorplans.
  return name ? `n:${name}` : `s:${fp.bedKey ?? '?'}:${fp.sqFt ?? '?'}`;
}

/**
 * Every floorplan the record evidences, from BOTH plan arrays.
 *
 * This used to read `raw.floorplans` alone while `normalizePricing` read
 * `firedev_floorplans` at tier 2 — so the same document could price a
 * bedroom while contributing no evidence that the bedroom exists. 137
 * buildings have firedev plans and no legacy array at all and rendered an
 * empty floorplan list; 751 more hide a size the legacy array omits.
 *
 * firedev wins a collision because the pricing ladder already ranks it above
 * `floorplans[]` — a fresh per-unit scrape over an older one. Letting the two
 * disagree about which plan is authoritative is how a card comes to show one
 * price in its list and another in its header.
 */
function normalizeFloorplans(raw: Record<string, unknown>): CanonicalFloorplan[] {
  const out: CanonicalFloorplan[] = [];
  const seen = new Set<string>();

  const addAll = (source: unknown) => {
    if (!Array.isArray(source)) return;
    for (const fp of source) {
      if (!fp || typeof fp !== 'object') continue;
      const plan = toFloorplan(fp as Record<string, unknown>);
      // A row with neither a bedroom nor a name carries no inventory
      // information; keeping it would pad the list with blanks.
      if (plan.bedKey == null && !plan.planName) continue;
      const id = floorplanIdentity(plan);
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(plan);
    }
  };

  addAll(raw.firedev_floorplans);
  addAll(raw.floorplans);
  return out;
}

function photoAttributionFor(
  sourceType: PhotoAttribution['sourceType'],
  r: Record<string, unknown>,
  idx: number,
  item: unknown,
): PhotoAttribution {
  let attributionHtml: string | null = null;
  if (sourceType === 'google') {
    let ref: Record<string, unknown> | null =
      item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
    if (!ref) {
      const gp = r.google_photos;
      if (Array.isArray(gp) && gp[idx] && typeof gp[idx] === 'object') {
        ref = gp[idx] as Record<string, unknown>;
      }
    }
    const atts = ref?.html_attributions;
    if (Array.isArray(atts)) {
      const joined = atts.filter((a) => typeof a === 'string' && a.trim()).join(' ');
      attributionHtml = joined || null;
    } else if (typeof atts === 'string' && atts.trim()) {
      attributionHtml = atts;
    }
  }
  let sourceDomain: string | null = null;
  let sourceUrl: string | null = null;
  if (sourceType === 'website' && item && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    sourceDomain = typeof o.source_domain === 'string' && o.source_domain.trim()
      ? o.source_domain : null;
    sourceUrl = typeof o.source_url === 'string' && o.source_url.trim()
      ? o.source_url : null;
  }
  return { sourceType, attributionHtml, sourceDomain, sourceUrl };
}

/* ─── Marketing photo discipline (mirrors backend _build_photos) ───────────
 * Product rule: the building leads, then the units; never marketing fluff,
 * non-apartment imagery, or photos with people in them.
 *
 * `photo_catalog` is a per-photo vision classification. The filename
 * heuristic below can't do this job — R2 URLs are /photos/<uuid>/3-hero.webp
 * and carry no semantics — so when a catalog exists it wins outright.
 */
const PHOTO_CATEGORY_RANK: Record<string, number> = {
  exterior: 0,
  interior_unit: 1,
  interior_common: 2,
  amenity: 3,
};
const PHOTO_EXCLUDED_CATEGORIES = new Set([
  'people', 'pet', 'branding', 'stock',
  'map_neighborhood', 'low_quality', 'floorplan', 'unclear',
]);
const PHOTO_ID_RE = /\/photos\/([^/]+)\/((?:website-)?\d+)(?:-[a-z]+)?(?:\.webp)?$/i;
const PHOTO_LEGACY_ID_RE = /\/static\/photos\/([^/]+)\/photo_(\d+)\.jpe?g$/i;

/** Preset-agnostic identity so a raw entry and its catalog row join. */
function photoIdentity(raw: unknown): string | null {
  let url: unknown = raw;
  if (url && typeof url === 'object') {
    const o = url as Record<string, unknown>;
    url = o.url ?? o.src ?? o.href ?? o.photo_url;
  }
  if (typeof url !== 'string' || !url) return null;
  const legacy = url.match(PHOTO_LEGACY_ID_RE);
  if (legacy) return `${legacy[1]}/${legacy[2]}`;
  const m = url.match(PHOTO_ID_RE);
  return m ? `${m[1]}/${m[2]}` : null;
}

interface PhotoCatalogEntry { category?: string; marketing_suitable?: boolean }

export function guidePhotoCategory(raw: Apartment | Record<string, unknown>, url: string): string | undefined {
  const identity = photoIdentity(url);
  if (!identity) return undefined;
  return photoCatalogIndex(raw as Record<string, unknown>).get(identity)?.category;
}

function photoCatalogIndex(r: Record<string, unknown>): Map<string, PhotoCatalogEntry> {
  const index = new Map<string, PhotoCatalogEntry>();
  const catalog = r.photo_catalog;
  if (!Array.isArray(catalog)) return index;
  for (const entry of catalog) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    for (const candidate of [e.key, e.url]) {
      const ident = photoIdentity(candidate);
      if (ident && !index.has(ident)) index.set(ident, e as PhotoCatalogEntry);
    }
  }
  return index;
}

/** Mirrors backend _build_photos: (urls, attribution) kept index-aligned. */
function buildPhotos(
  raw: Apartment | Record<string, unknown>,
  preset: PhotoPreset = 'card',
): { urls: string[]; attribution: PhotoAttribution[] } {
  const r = raw as Record<string, unknown>;
  const urls: string[] = [];
  const attribution: PhotoAttribution[] = [];
  const seen = new Set<string>();
  // Source quality, used only as a TIEBREAKER now: website (apartment site →
  // R2) > google (Places) > legacy.
  const SOURCE_RANK: Record<string, number> = { website: 0, google: 1, legacy: 2 };
  type PhotoCand = { item: unknown; idx: number; score: number; sourceType: PhotoAttribution['sourceType']; rank: number };
  const cands: PhotoCand[] = [];
  const collect = (items: unknown, sourceType: PhotoAttribution['sourceType']) => {
    if (!Array.isArray(items)) return;
    items.forEach((item, idx) => {
      cands.push({ item, idx, score: photoQualityScore(item), sourceType, rank: SOURCE_RANK[sourceType] ?? 9 });
    });
  };
  collect(r.website_photos, 'website');
  collect(r.local_photos, 'google');
  collect(r.google_photos, 'google');
  collect(r.photos, 'legacy');
  // Global sort: the best content (a building EXTERIOR scores +56) leads
  // regardless of source — so the lead photo is the building, not whatever
  // hero the marketing site happened to put first. Source quality and original
  // order only break ties.
  const catalog = photoCatalogIndex(r);
  if (catalog.size > 0) {
    // Classified doc: the vision catalog beats every filename heuristic.
    // Drop excluded imagery outright, then lead with the building.
    const ranked = cands
      .map((c, order) => {
        const entry = catalog.get(photoIdentity(c.item) ?? '');
        if (!entry) {
          // Added since the last classification run — keep it, but never let
          // it outrank a verified exterior.
          return { c, order, rank: Object.keys(PHOTO_CATEGORY_RANK).length + 1, drop: false };
        }
        const category = String(entry.category ?? '').toLowerCase();
        const drop = entry.marketing_suitable === false || PHOTO_EXCLUDED_CATEGORIES.has(category);
        const rank = PHOTO_CATEGORY_RANK[category] ?? Object.keys(PHOTO_CATEGORY_RANK).length;
        return { c, order, rank, drop };
      })
      .filter((x) => !x.drop)
      .sort((a, b) => a.rank - b.rank || a.order - b.order);
    cands.length = 0;
    cands.push(...ranked.map((x) => x.c));
  } else {
    cands.sort((a, b) => b.score - a.score || a.rank - b.rank || a.idx - b.idx);
  }
  for (const { item, idx, score, sourceType } of cands) {
    if (catalog.size === 0 && score < 0) continue;
    const url = resolvePhotoUrl(item, preset);
    if (url && !seen.has(url)) {
      urls.push(url);
      seen.add(url);
      attribution.push(photoAttributionFor(sourceType, r, idx, item));
    }
  }
  // Not-aligned (P2-backfilled): suppress per-photo google credit (would
  // name the wrong contributor); surfaced place-level instead.
  if (r.google_photos_attribution_aligned === false) {
    for (const a of attribution) {
      if (a.sourceType === 'google') a.attributionHtml = null;
    }
  }
  return { urls, attribution };
}

/** Deduped, order-stable place-level Google attribution — only for
 *  not-aligned docs (else empty). Mirrors backend _place_attribution. */
function placeAttribution(r: Record<string, unknown>): string[] {
  if (r.google_photos_attribution_aligned !== false) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const gp = r.google_photos;
  if (!Array.isArray(gp)) return [];
  for (const ref of gp) {
    if (!ref || typeof ref !== 'object') continue;
    const atts = (ref as Record<string, unknown>).html_attributions;
    const items = Array.isArray(atts) ? atts : typeof atts === 'string' ? [atts] : [];
    for (const a of items) {
      if (typeof a === 'string' && a.trim() && !seen.has(a)) {
        seen.add(a);
        out.push(a);
      }
    }
  }
  return out;
}

export function normalizePhotoUrls(
  raw: Apartment | Record<string, unknown>,
  preset: PhotoPreset = 'card',
): string[] {
  return buildPhotos(raw, preset).urls;
}

/**
 * Photos + index-aligned provenance for a given preset. Use this (not
 * ad-hoc per-component photo assembly) anywhere attribution must show —
 * keeps the credit correctly paired with each image.
 */
export function normalizePhotosWithAttribution(
  raw: Apartment | Record<string, unknown>,
  preset: PhotoPreset = 'card',
): { urls: string[]; attribution: PhotoAttribution[]; place: string[] } {
  const { urls, attribution } = buildPhotos(raw, preset);
  return { urls, attribution, place: placeAttribution(raw as Record<string, unknown>) };
}

/**
 * Trigger browser image cache for the first N exterior-sorted photos. Fire
 * from marker hover so the detail modal opens with photos already warm.
 * Idempotent: the browser cache dedupes repeat requests.
 */
const prefetchedUrls = new Set<string>();
export function prefetchPhotos(
  raw: Apartment | Record<string, unknown>,
  preset: PhotoPreset = 'hero',
  count = 3,
): void {
  if (typeof window === 'undefined') return;
  const urls = normalizePhotoUrls(raw, preset).slice(0, count);
  for (const url of urls) {
    if (!url || prefetchedUrls.has(url)) continue;
    prefetchedUrls.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
}

// ── Top-level normalizer ────────────────────────────────────────────

/**
 * Convert a raw `Apartment` record into the canonical shape. Pure
 * function — does not mutate `raw`. Throws if `name` is missing.
 */
export function normalizeApartment(raw: Apartment | Record<string, unknown>): CanonicalApartment {
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string' || !r.name) {
    throw new Error("normalizeApartment: required field 'name' is missing");
  }
  const builtPhotos = buildPhotos(r);
  const pricesDetailed = normalizePricingDetailed(r);
  return {
    name: r.name,
    city: r.city as string | undefined,
    neighborhood: r.neighborhood as string | undefined,
    region: r.region as string | undefined,
    address: r.address as string | undefined,
    latitude: r.latitude as number | undefined,
    longitude: r.longitude as number | undefined,
    photos: builtPhotos.urls,
    photoAttribution: builtPhotos.attribution,
    photoAttributionPlace: placeAttribution(r),
    startingPrices: canonicalPricesOf(pricesDetailed),
    startingPricesProvenance: pricesDetailed,
    floorplans: normalizeFloorplans(r),
    yearBuilt: (r.year_built as number) ?? (r.built_year as number) ?? undefined,
    totalUnits: r.total_units as number | undefined,
    buildingType: r.building_type as string | undefined,
    walkScore: r.walk_score as number | undefined,
    transitScore: r.transit_score as number | undefined,
    rating: r.rating as number | undefined,
    reviewCount: r.review_count as number | undefined,
    isTacoFavorite: !!r.is_taco_favorite,
    isPetFriendly: derivePetFriendly(r),
    leasingSpecialShort: parseLeasingSpecial(r.leasing_specials) ?? undefined,
    leasingSpecialsRaw: r.leasing_specials as string | undefined,
    pricingSource: r.pricing_source as string | undefined,
    pricingUpdatedAt: r.pricing_updated_at as string | undefined,
    pricingUnavailable: isPricingUnavailable(r),
    stalePriceDisplay: (() => {
      if (!isPricingUnavailable(r)) return undefined;
      const numeric = r.starting_price_numeric as number | undefined;
      if (numeric && numeric > 0) return `~$${numeric.toLocaleString()}`;
      const text = String(r.starting_price ?? '').trim();
      const match = text.match(/[\d,]+/);
      if (match) {
        const parsed = parseInt(match[0].replace(/,/g, ''), 10);
        if (parsed > 400) return `~$${parsed.toLocaleString()}`;
      }
      return undefined;
    })(),
    pricingInvalidatedReason: r.pricing_invalidated_reason as string | undefined,
    pricingInvalidatedAt: r.pricing_invalidated_at as string | undefined,
  };
}

// ── CardRenderContext builder ───────────────────────────────────────

/**
 * Build a `CardRenderContext` from the raw search-result inputs at the
 * API boundary. Pairs with `normalizeApartment` per ADR-0008.
 *
 * Inputs:
 *   - `rawApartment`: the apartment record (may have legacy inlined
 *     fields like `ai_analysis`, `ai_tier`, `ranking_source`).
 *   - `explanation`: per-apartment explanation dict (the backend's
 *     PR 2 composer output: `headline`, `recommendation`, `aiTier`).
 *     Optional — some surfaces (e.g., Vibe results) inline explanation
 *     fields directly on the apartment record instead.
 *   - `currentFilters`: active Search criteria; only `bedrooms` is
 *     read today and normalized via `normalizeBedKey`.
 *
 * Field-source priority:
 *   - `headline`    ← `explanation.headline` → empty.
 *   - `recommendation` ← `explanation.recommendation` →
 *                       `rawApartment.ai_analysis` (legacy inlined)
 *                       → empty.
 *   - `aiTier`      ← `explanation.aiTier` → `explanation.tier`
 *                       → `rawApartment.ai_tier`.
 *   - `rankingSource` ← `rawApartment.ranking_source`.
 *   - `bedrooms`    ← `currentFilters.bedrooms` normalized through
 *                       `normalizeBedKey`.
 *
 * Returns `undefined` when there is no useful personalization data
 * (no explanation content, no bedrooms criteria, no AI gating
 * metadata). Cards rendered without a `CardRenderContext` fall back
 * to building-cheapest prices and no Headline/Recommendation
 * section — the correct behavior for saved-list view and admin
 * tools per ADR-0008.
 */
export function mapToCardRenderContext(
  rawApartment: Apartment | Record<string, unknown>,
  explanation?: Record<string, unknown> | null,
  currentFilters?: { bedrooms?: unknown },
): CardRenderContext | undefined {
  const apt = rawApartment as Record<string, unknown>;
  const exp = (explanation ?? {}) as Record<string, unknown>;

  let bedrooms: CanonicalBedKey[] | undefined;
  const rawBedrooms = currentFilters?.bedrooms;
  if (Array.isArray(rawBedrooms) && rawBedrooms.length > 0) {
    const normed = rawBedrooms
      .map((b) => normalizeBedKey(b))
      .filter((k): k is CanonicalBedKey => !!k);
    if (normed.length > 0) bedrooms = normed;
  }

  const headlineRaw = exp.headline;
  const headline =
    typeof headlineRaw === 'string' && headlineRaw.trim() ? headlineRaw : '';

  let recommendation = '';
  const recRaw = exp.recommendation;
  if (typeof recRaw === 'string' && recRaw.trim()) {
    recommendation = recRaw;
  } else {
    const aiAnalysis = apt.ai_analysis;
    if (typeof aiAnalysis === 'string' && aiAnalysis.trim()) {
      recommendation = aiAnalysis;
    }
  }

  let aiTier: number | undefined;
  if (typeof exp.aiTier === 'number') {
    aiTier = exp.aiTier;
  } else if (typeof exp.tier === 'number') {
    aiTier = exp.tier;
  } else if (typeof apt.ai_tier === 'number') {
    aiTier = apt.ai_tier;
  } else if (typeof apt.ai_tier === 'string') {
    const parsed = parseInt(apt.ai_tier, 10);
    if (!Number.isNaN(parsed)) aiTier = parsed;
  }

  let rankingSource: string | undefined;
  if (typeof apt.ranking_source === 'string' && apt.ranking_source.trim()) {
    rankingSource = apt.ranking_source;
  }

  if (
    !headline &&
    !recommendation &&
    !bedrooms &&
    aiTier === undefined &&
    !rankingSource
  ) {
    return undefined;
  }

  return {
    headline,
    recommendation,
    bedrooms,
    aiTier,
    rankingSource,
  };
}
